import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Collapse,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import gsap from 'gsap';
import { useTranslation } from 'react-i18next';
import { todoAPI } from '../services/api';
import { prefersReducedMotion } from '../utils/motion';
import fields from './todoFields.module.css';
import styles from './TodoItem.module.css';

function Tag({ label, bg, color }) {
  return (
    <span
      className={styles.tag}
      style={{ '--tag-bg': bg, '--tag-color': color }}
    >
      {label}
    </span>
  );
}

function getTags(todo, t, locale) {
  const tags = [];
  if (todo.urgency === 'HIGH') {
    tags.push({ label: t('task.tags.urgent'), bg: 'var(--tag-urgent-bg)', color: 'var(--tag-urgent-text)' });
  } else if (todo.urgency === 'MEDIUM') {
    tags.push({ label: t('task.tags.medium'), bg: 'var(--tag-medium-bg)', color: 'var(--tag-medium-text)' });
  } else if (todo.urgency === 'LOW') {
    tags.push({ label: t('task.tags.low'), bg: 'var(--tag-low-bg)', color: 'var(--tag-low-text)' });
  }
  if (todo.dueDate) {
    const d = new Date(todo.dueDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const label = d
      .toLocaleDateString(locale, { month: 'short', day: 'numeric' })
      .toLowerCase();
    let bg = 'var(--tag-default-bg)';
    let color = 'var(--tag-default-text)';
    if (d.getTime() < today.getTime()) {
      bg = 'var(--tag-urgent-bg)';
      color = 'var(--tag-urgent-text)';
    } else if (d.getTime() === today.getTime()) {
      bg = 'var(--tag-medium-bg)';
      color = 'var(--tag-medium-text)';
    }
    tags.push({ label, bg, color });
  }
  return tags;
}

const TodoItem = ({ todo, onUpdate, onDelete, onToggle }) => {
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [dueDateFocused, setDueDateFocused] = useState(false);
  const [title, setTitle] = useState(todo.title);
  const [description, setDescription] = useState(todo.description || '');
  const [urgency, setUrgency] = useState(todo.urgency || '');
  const [dueDate, setDueDate] = useState(todo.dueDate || '');
  const [completed, setCompleted] = useState(todo.completed);
  const [loading, setLoading] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: todo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const rowRef = useRef(null);
  const checkboxRef = useRef(null);
  const hasRenderedRef = useRef(false);
  useEffect(() => {
    if (!hasRenderedRef.current) {
      hasRenderedRef.current = true;
      return undefined;
    }
    const el = checkboxRef.current;
    if (!el || prefersReducedMotion()) return undefined;
    const tween = gsap.fromTo(
      el,
      { scale: completed ? 0.5 : 1.3 },
      { scale: 1, duration: 0.45, ease: 'back.out(3)', clearProps: 'transform' }
    );
    return () => tween.kill();
  }, [completed]);

  const handleToggleComplete = async () => {
    const newCompleted = !completed;
    setCompleted(newCompleted);
    if (onToggle) onToggle(todo.id);
    try {
      await todoAPI.update(todo.id, title, description, newCompleted, todo.areaId, urgency, dueDate);
    } catch (error) {
      console.error('Error updating todo:', error);
      setCompleted(!newCompleted);
      if (onToggle) onToggle(todo.id); // revert parent state too
    }
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setLoading(true);
    try {
      await todoAPI.update(todo.id, title, description, completed, todo.areaId, urgency, dueDate);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating todo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setTitle(todo.title);
    setDescription(todo.description || '');
    setUrgency(todo.urgency || '');
    setDueDate(todo.dueDate || '');
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (window.confirm(t('task.deleteConfirm'))) {
      setLoading(true);
      try {
        await todoAPI.delete(todo.id);
        if (onDelete) onDelete();
      } catch (error) {
        console.error('Error deleting todo:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const tags = getTags(todo, t, i18n.language);

  return (
    <Box ref={setNodeRef} style={style}>
      {/* Opening morphs the row into the form: the row hides instantly while the
          form starts collapsed at the row's height, keeping the total height
          continuous. Closing animates both so the form folds back into the row. */}
      <Collapse
        in={isEditing}
        unmountOnExit
        collapsedSize={isEditing && rowRef.current ? rowRef.current.offsetHeight : 0}
      >
        <Box className={fields.card}>
          <TextField
            fullWidth
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            className={`${fields.field} ${fields.mb1}`}
          />
          <TextField
            fullWidth
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            placeholder={t('task.descriptionPlaceholder')}
            className={`${fields.field} ${fields.mb1}`}
          />
          <Box className={fields.editRow}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('task.urgencyLabel')}
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
              SelectProps={{ MenuProps: { disableScrollLock: true } }}
              className={fields.field}
            >
              {['', 'LOW', 'MEDIUM', 'HIGH'].map((v) => (
                <MenuItem key={v} value={v} className={fields.menuItem}>
                  {t(`task.urgency.${(v || 'none').toLowerCase()}`)}
                </MenuItem>
              ))}
            </TextField>
            <Box className={fields.dueWrap}>
              <TextField
                fullWidth
                size="small"
                label={t('task.dueDateLabel')}
                type={dueDate || dueDateFocused ? 'date' : 'text'}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                onFocus={() => setDueDateFocused(true)}
                onBlur={() => setDueDateFocused(false)}
                InputLabelProps={{ shrink: dueDate || dueDateFocused ? true : undefined }}
                className={fields.field}
              />
              {dueDate && (
                <IconButton size="small" onClick={() => setDueDate('')} className={fields.clearDateBtn}>
                  <CancelIcon className={fields.clearDateIcon} />
                </IconButton>
              )}
            </Box>
          </Box>
          <Box className={fields.editActions}>
            <Button
              size="small"
              onClick={handleSave}
              disabled={loading || !title.trim()}
              className={fields.saveBtn}
            >
              {t('common.save')}
            </Button>
            <Button
              size="small"
              onClick={handleCancel}
              disabled={loading}
              className={fields.cancelBtn}
            >
              {t('common.cancel')}
            </Button>
          </Box>
        </Box>
      </Collapse>

      <Collapse in={!isEditing} timeout={{ enter: 300, exit: 0 }}>
        <Box ref={rowRef} className={styles.row}>
          {/* Drag handle */}
          <Box {...attributes} {...listeners} className={styles.dragHandle}>
            <DragIndicatorIcon className={styles.dragIcon} />
          </Box>

          {/* Custom checkbox */}
          <Box
            ref={checkboxRef}
            onClick={handleToggleComplete}
            className={`${styles.checkbox} ${completed ? styles.checkboxDone : ''}`}
          >
            {completed && <span className={styles.check}>✓</span>}
          </Box>

          {/* Title */}
          <Typography
            onClick={() => !completed && setIsEditing(true)}
            className={`${styles.title} ${completed ? styles.titleDone : ''}`}
          >
            <span className={styles.titleText}>{todo.title}</span>
          </Typography>

          {/* Tags */}
          {tags.map((tag, i) => (
            <Tag key={i} label={tag.label} bg={tag.bg} color={tag.color} />
          ))}

          {/* Delete control */}
          <Box className={styles.deleteWrap}>
            {completed && (
              <IconButton
                size="small"
                onClick={handleDelete}
                disabled={loading}
                className={styles.deleteBtn}
              >
                <DeleteIcon className={styles.deleteIcon} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default TodoItem;
